import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../AuthenticatedApp';
import { useVenueId } from '../hooks/useUserData';
import { useVenueFeedback } from '../context/VenueFeedbackContext';
import {
  openVenueShift,
  getCurrentVenueShift,
  closeVenueShift,
} from '../utils/staffApiService';
import '../styles/ShiftPage.css';

const POLL_INTERVAL_MS = 15000;

const formatCurrency = (value) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(value || 0);

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ShiftPage = () => {
  const venueId = useVenueId();
  const { getAccessTokenSilently } = useAuth0();
  const { userPermissions = [] } = useAuth();
  const { pushFeedback } = useVenueFeedback();
  const hasShiftAccess = userPermissions.includes('manage_shift');

  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [actualCash, setActualCash] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const pollRef = useRef(null);

  const fetchCurrentShift = useCallback(async (silent = false) => {
    if (!venueId) return;
    try {
      const data = await getCurrentVenueShift(venueId, getAccessTokenSilently);
      setShift(data);
    } catch (err) {
      if (err.code === 'NO_OPEN_SHIFT' || err.status === 404) {
        setShift(null);
      } else if (!silent) {
        pushFeedback({ type: 'error', title: 'Vardiya yüklenemedi', message: err.message || 'Vardiya bilgisi alınamadı.' });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [venueId, getAccessTokenSilently, pushFeedback]);

  useEffect(() => {
    if (hasShiftAccess) {
      fetchCurrentShift();
    } else {
      setLoading(false);
    }
  }, [hasShiftAccess, fetchCurrentShift]);

  useEffect(() => {
    if (!shift || shift.status !== 'open') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => fetchCurrentShift(true), POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [shift, fetchCurrentShift]);

  const handleOpenShift = async (e) => {
    e.preventDefault();
    const cash = parseFloat(openingCash);
    if (isNaN(cash) || cash < 0) {
      pushFeedback({ type: 'error', title: 'Geçersiz tutar', message: 'Açılış tutarı sıfır veya pozitif olmalıdır.' });
      return;
    }
    setSubmitting(true);
    try {
      const data = await openVenueShift(venueId, { openingCash: cash }, getAccessTokenSilently);
      setShift(data);
      setOpeningCash('');
      pushFeedback({ type: 'success', title: 'Vardiya açıldı', message: 'Yeni vardiya başlatıldı.' });
    } catch (err) {
      if (err.code === 'SHIFT_ALREADY_OPEN') {
        pushFeedback({ type: 'error', title: 'Vardiya zaten açık', message: 'Bu mekanda zaten açık bir vardiya var.' });
        fetchCurrentShift();
      } else {
        pushFeedback({ type: 'error', title: 'Vardiya açılamadı', message: err.message || 'Vardiya açılamadı.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseShift = async () => {
    const cash = parseFloat(actualCash);
    if (isNaN(cash) || cash < 0) {
      pushFeedback({ type: 'error', title: 'Geçersiz tutar', message: 'Kasadaki nakit tutarı sıfır veya pozitif olmalıdır.' });
      return;
    }
    setSubmitting(true);
    try {
      await closeVenueShift(
        venueId,
        { actualCash: cash, notes: closeNotes || null },
        getAccessTokenSilently,
      );
      setShift(null);
      setShowCloseModal(false);
      setActualCash('');
      setCloseNotes('');
      pushFeedback({ type: 'success', title: 'Vardiya kapatıldı', message: 'Vardiya başarıyla kapatıldı.' });
    } catch (err) {
      pushFeedback({ type: 'error', title: 'Vardiya kapatılamadı', message: err.message || 'Vardiya kapatılamadı.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="venue-page shift-page">
        <div className="shift-card shift-loading">
          <Spinner animation="border" size="sm" /> Yükleniyor...
        </div>
      </div>
    );
  }

  if (!hasShiftAccess) {
    return (
      <div className="venue-page shift-page">
        <section className="venue-surface-card venue-surface-card--padded venue-empty-state venue-empty-state--compact">
          <div className="venue-page-eyebrow">Vardiya</div>
          <h2 className="venue-page-title">Bu ekran yalnızca vardiya yöneticileri içindir</h2>
          <p className="venue-page-subtitle">
            Bu hesabın Vardiya ekranına girebilmesi için <code>manage_shift</code> yetkisi gerekir.
          </p>
        </section>
      </div>
    );
  }

  const liveCash = shift?.paymentSummary?.totalCash ?? 0;
  const liveCard = shift?.paymentSummary?.totalCard ?? 0;
  const liveOther = shift?.paymentSummary?.totalOther ?? 0;
  const liveRevenue = shift?.paymentSummary?.totalRevenue ?? 0;
  const liveChecks = shift?.paymentSummary?.checksClosedCount ?? 0;
  const liveExpectedCash = (shift?.openingCash ?? 0) + liveCash;

  const closeActualParsed = parseFloat(actualCash);
  const closeVariance = !isNaN(closeActualParsed) ? closeActualParsed - liveExpectedCash : null;
  const varianceClass =
    closeVariance === null || closeVariance === 0
      ? 'shift-variance-preview--match'
      : closeVariance > 0
        ? 'shift-variance-preview--surplus'
        : 'shift-variance-preview--deficit';

  /* ── No open shift ──────────────────────────────────── */

  if (!shift) {
    return (
      <div className="venue-page shift-page">
        <div className="shift-closed-notice">
          Vardiya kapalı — kasa işlemleri vardiya dışında kaydedilecek ve mutabakat raporuna dahil olmayacak.
        </div>

        <div className="shift-card">
          <div className="shift-open-header">
            <div className="shift-open-header__eyebrow">Vardiya</div>
            <div className="shift-open-header__title">Yeni Vardiya Başlat</div>
          </div>

          <Form className="shift-open-form" onSubmit={handleOpenShift}>
            <Form.Group>
              <span className="shift-open-form__label">Kasa Açılış Tutarı</span>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                placeholder="0.00"
                required
              />
            </Form.Group>
            <Button type="submit" className="shift-open-form__submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : 'Vardiyayı Aç'}
            </Button>
          </Form>
        </div>
      </div>
    );
  }

  /* ── Open shift dashboard ───────────────────────────── */

  return (
    <div className="venue-page shift-page">
      <div className="shift-card">
        <div className="shift-live-header">
          <div>
            <div className="shift-live-header__eyebrow">Açık Vardiya</div>
            <div className="shift-live-header__meta">
              {formatTime(shift.openedAt)} &middot; {shift.openedByUserId}
            </div>
          </div>
          <span className="shift-status-chip shift-status-chip--open">Aktif</span>
        </div>

        <div className="shift-hero-metric">
          <div className="shift-hero-metric__label">Toplam Ciro</div>
          <div className="shift-hero-metric__value">{formatCurrency(liveRevenue)}</div>
        </div>

        <div className="shift-breakdown">
          <div className="shift-breakdown__item">
            <div className="shift-breakdown__label">Nakit</div>
            <div className="shift-breakdown__value">{formatCurrency(liveCash)}</div>
          </div>
          <div className="shift-breakdown__item">
            <div className="shift-breakdown__label">Kart</div>
            <div className="shift-breakdown__value">{formatCurrency(liveCard)}</div>
          </div>
          <div className="shift-breakdown__item">
            <div className="shift-breakdown__label">Diğer</div>
            <div className="shift-breakdown__value">{formatCurrency(liveOther)}</div>
          </div>
        </div>

        <hr className="shift-divider" />

        <div className="shift-stats-row">
          <div className="shift-stats-row__item">
            <span className="shift-stats-row__label">Açılış Kasa</span>
            <span className="shift-stats-row__value">{formatCurrency(shift.openingCash)}</span>
          </div>
          <div className="shift-stats-row__item">
            <span className="shift-stats-row__label">Beklenen Nakit</span>
            <span className="shift-stats-row__value">{formatCurrency(liveExpectedCash)}</span>
          </div>
          <div className="shift-stats-row__item">
            <span className="shift-stats-row__label">Adisyon</span>
            <span className="shift-stats-row__value">{liveChecks}</span>
          </div>
        </div>

        <hr className="shift-divider" />

        <Button
          className="shift-close-trigger"
          onClick={() => { setActualCash(''); setCloseNotes(''); setShowCloseModal(true); }}
          disabled={submitting}
        >
          Vardiyayı Kapat
        </Button>
      </div>

      {/* ── Close modal ──────────────────────────────── */}

      <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)} centered className="shift-close-modal">
        <Modal.Header closeButton>
          <Modal.Title>Vardiyayı Kapat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="shift-close-summary">
            <div className="shift-close-summary-row">
              <span>Açılış Kasa</span>
              <strong>{formatCurrency(shift.openingCash)}</strong>
            </div>
            <div className="shift-close-summary-row">
              <span>Nakit Ödemeler</span>
              <strong>{formatCurrency(liveCash)}</strong>
            </div>
            <div className="shift-close-summary-row">
              <span>Beklenen Nakit</span>
              <strong>{formatCurrency(liveExpectedCash)}</strong>
            </div>
          </div>

          <div className="shift-input-group">
            <span className="shift-input-group__label">Kasadaki Gerçek Nakit</span>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={actualCash}
              onChange={(e) => setActualCash(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
          </div>

          {closeVariance !== null && (
            <div className={`shift-variance-preview ${varianceClass}`}>
              <span>Fark</span>
              <span>{closeVariance >= 0 ? '+' : ''}{formatCurrency(closeVariance)}</span>
            </div>
          )}

          <div className="shift-close-notes">
            <span className="shift-close-notes__label">Notlar (opsiyonel)</span>
            <textarea
              value={closeNotes}
              onChange={(e) => setCloseNotes(e.target.value)}
              placeholder="Vardiya kapanış notu..."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="shift-modal-cancel" onClick={() => setShowCloseModal(false)}>
            İptal
          </Button>
          <Button className="shift-modal-confirm" onClick={handleCloseShift} disabled={submitting || actualCash === ''}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Vardiyayı Kapat ve Onayla'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ShiftPage;
